import React from 'react';
import { amountToWordsSoles } from '../../utils/amountToWords';
import {
  formatPrintDate,
  formatPrintDateOrDash,
  resolveOrderPaymentPrintDate,
} from '../../utils/dateUtils';

const formatQty = (value) =>
  parseFloat(value || 0).toLocaleString('es-PE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 3,
  });

const formatMoney = (n) =>
  parseFloat(n || 0).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const formatUnitPrice = (p) =>
  parseFloat(p || 0).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 4 });

function buildPaymentCondition(order) {
  if (order.payment_condition_type === 'cash') return 'Al contado';
  if (order.payment_condition_type === 'days') {
    return `Cada ${order.payment_condition_days} días`;
  }
  if (order.payment_condition_type === 'date' && order.payment_condition_date) {
    return `Fecha específica: ${formatPrintDate(order.payment_condition_date)}`;
  }
  return '-';
}

function docTypeLabel(client) {
  if (client?.document_type === 'ruc') return 'RUC';
  if (client?.document_type === 'dni') return 'DNI';
  if (client?.document_type) return String(client.document_type).toUpperCase();
  return 'Documento';
}

function getItemDescription(item) {
  const notes = item.notes || item.description;
  if (notes && String(notes).trim()) return String(notes).trim();
  return item.service?.name || 'Servicio';
}

const SalesServiceOrderPrintDocument = ({ order }) => {
  if (!order) return null;

  const statusTone = order.status === 'cancelled' ? 'cancelled' : 'issued';
  const statusLabel = order.status === 'cancelled' ? 'Cancelada' : 'Emitida';
  const paymentDateNumeric = resolveOrderPaymentPrintDate(order);
  const igvRatePercent = parseFloat(order.items?.[0]?.igv_rate ?? 18);

  const scheduleParts = [];
  if (order.delivery_date) scheduleParts.push(`Entrega: ${formatPrintDate(order.delivery_date)}`);
  if (order.service_start_date) scheduleParts.push(`Inicio: ${formatPrintDate(order.service_start_date)}`);
  if (order.service_end_date) scheduleParts.push(`Fin: ${formatPrintDate(order.service_end_date)}`);

  const posLabel = order.point_of_sale
    ? [order.point_of_sale.code, order.point_of_sale.name].filter(Boolean).join(' — ')
    : '—';

  const items = (order.items || []).filter((item) => item.item_type === 'service' || item.service_id);

  return (
    <div className="print-container">
      <h1 className="doc-title">Orden de servicio (venta)</h1>
      <hr className="doc-title-rule" />

      <div className="info-line">
        <span className="info-label">Orden:</span> {order.code || '—'}
        <span className="info-sep">·</span>
        <span className="info-label">Fecha:</span> {formatPrintDateOrDash(order.issue_date)}
        {order.sales_quote?.code && (
          <>
            <span className="info-sep">·</span>
            <span className="info-label">Cotización:</span> {order.sales_quote.code}
          </>
        )}
        <span className="info-sep">·</span>
        <span className="info-label">Estado:</span>{' '}
        <span className={`status-badge status-${statusTone}`}>{statusLabel}</span>
      </div>

      <div className="info-line">
        <span className="info-label">Cliente:</span> {order.client?.name || 'N/A'}
        {order.client?.document_number && (
          <>
            <span className="info-sep">·</span>
            <span className="info-label">{docTypeLabel(order.client)}:</span> {order.client.document_number}
          </>
        )}
      </div>

      <div className="info-line">
        <span className="info-label">Punto de venta:</span> {posLabel}
      </div>

      <div className="info-line">
        <span className="info-label">Condición de pago:</span> {buildPaymentCondition(order)}
        {paymentDateNumeric && order.payment_condition_type !== 'cash' && (
          <>
            <span className="info-sep">·</span>
            <span className="info-label">Fecha de pago:</span> {paymentDateNumeric}
          </>
        )}
      </div>

      {scheduleParts.length > 0 && (
        <div className="info-line">
          <span className="info-label">Plazos:</span> {scheduleParts.join(' · ')}
        </div>
      )}

      <div className="items-section">
        <div className="section-title">Servicios de la orden</div>

        <div className="items-table-wrap">
          <table className="items-table cols-6">
            <thead>
              <tr>
                <th>Descripción</th>
                <th className="center">Unidad</th>
                <th>Proyecto</th>
                <th className="num">Cant.</th>
                <th className="num">P. Unit</th>
                <th className="num">Subtotal</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr>
                  <td colSpan={6}>Sin servicios registrados</td>
                </tr>
              ) : (
                items.map((item, index) => (
                  <tr key={item.id ?? index}>
                    <td>{getItemDescription(item)}</td>
                    <td className="center">{item.unit_of_measure || item.service?.unit_of_measure || 'UND'}</td>
                    <td>
                      {item.project
                        ? `${item.project.code || ''}${item.project.code && item.project.name ? ' - ' : ''}${item.project.name || ''}`
                        : '—'}
                    </td>
                    <td className="num">{formatQty(item.quantity)}</td>
                    <td className="num">S/ {formatUnitPrice(item.unit_price)}</td>
                    <td className="subtotal-cell">S/ {formatMoney(item.subtotal ?? item.total)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="print-footer">
          <div className="print-footer-obs">
            <div className="print-obs-label">Observaciones:</div>
            {order.notes ? <div className="print-obs-text">{order.notes}</div> : null}
          </div>
          <div className="print-footer-total">
            <div className="print-total-row">
              <span className="print-total-label">Base:</span>
              <span className="print-total-value">S/ {formatMoney(order.base_imponible)}</span>
            </div>
            <div className="print-total-row">
              <span className="print-total-label">IGV ({igvRatePercent}%):</span>
              <span className="print-total-value">S/ {formatMoney(order.igv_amount)}</span>
            </div>
            {parseFloat(order.no_gravados || 0) > 0 && (
              <div className="print-total-row">
                <span className="print-total-label">No grav.:</span>
                <span className="print-total-value">S/ {formatMoney(order.no_gravados)}</span>
              </div>
            )}
            <div className="print-total-row print-total-row-final">
              <span className="print-total-label">Total:</span>
              <span className="print-total-value">S/ {formatMoney(order.total_amount)}</span>
            </div>
            <div className="print-totals-words">Son: {amountToWordsSoles(order.total_amount)}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SalesServiceOrderPrintDocument;
